import { make } from 'vuex-pathify'
import store from '../index'
import { getQueryFromFilter, showError, showSuccess } from '@/util/helpers'
import { formatDate } from '@/util/formatters'
import {
  formatToNumber,
  formatToBRL,
  formatToDateTime,
  formatToDate,
} from 'brazilian-values'
import axios from 'axios'
import pdfTemplate from '@/util/pdfTemplate'

const state = {
  purchase: {
    provider: {},
    shippingCompany: {},
    products: [],
    payments: [],
  },

  purchases: {
    data: [],
    count: 0,
    limit: 50,
  },

  filter: {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['createdAt'],
    sortDesc: [true],
  },

  type: 'add',
  loading: false,
  dialog: false,
  importedXmlWithoutPayments: false,
}

const mutations = make.mutations(state)

const actions = {
  add: ({ state }, data = null) => {
    state.importedXmlWithoutPayments = false
    state.purchase = {
      ...data,

      provider: null,
      shippingCompany: null,
      products: [],
      payments: [],
      notaFiscalDate: null,
      deliveryDate: formatDate(new Date().toISOString().substr(0, 10)),
      firstDueDate: formatDate(new Date().toISOString().substr(0, 10)),
      quotas: 1,
      valueProducts: 0,
      discount: 0,
      addition: 0,
      shippingValue: 0,
      otherExpenses: 0,
      value: 0,

      userId: store.get('authStore@currentUser').id,
      companyId: store.get('authStore@currentCompany').id,
    }

    state.type = 'add'
    state.dialog = true

    store.dispatch('purchaseStore/loadCombos')
  },

  edit: ({ state }, { id, type = 'edit' }) => {
    state.importedXmlWithoutPayments = false
    state.loading = true
    store.dispatch('purchaseStore/getById', id)
      .then(res => {
        state.purchase = res.data

        state.type = type
        state.dialog = true

        // format data
        state.purchase.deliveryDate = formatDate(new Date(res.data.deliveryDate).toISOString().substr(0, 10))
        state.purchase.firstDueDate = formatDate(new Date(res.data.firstDueDate).toISOString().substr(0, 10))
        state.purchase.notaFiscalDate = state.purchase.notaFiscalDate ? formatDate(new Date(state.purchase.notaFiscalDate).toISOString().substr(0, 10)) : null

        state.purchase.payments = state.purchase.payments.map(item => {
          item.dueDate = formatDate(new Date(item.dueDate).toISOString().substr(0, 10))
          item.paymentDate = item.paymentDate ? formatDate(new Date(item.paymentDate).toISOString().substr(0, 10)) : null

          item.dueMenu = false
          item.paymentMenu = false
          item.date = null

          return item
        })

        store.dispatch('purchaseStore/loadCombos')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  loadCombos: async () => {
    store.dispatch('paymentMethodStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('userStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('providerStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('productStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('productStore/getCfops', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['cfop'],
      sortDesc: [false],
    })

    store.dispatch('productStore/getNcms', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['ncm'],
      sortDesc: [false],
    })

    store.dispatch('discountListStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('unityStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('shippingCompanyStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['name'],
      sortDesc: [false],
    })

    store.dispatch('accountStore/get', {
      page: 1,
      itemsPerPage: 10000,
      sortBy: ['description'],
      sortDesc: [false],
    })
  },

  save: ({ state }, purchase) => {
    const id = purchase.id
    const method = id ? 'put' : 'post'

    state.loading = true
    return axios[method](`${process.env.VUE_APP_API_URL}/purchase/${id || ''}`, purchase)
      .then((res) => {
        state.dialog = false
        store.dispatch('purchaseStore/get')
        showSuccess('Compra salva com sucesso!')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  export: ({ state }, id) => {
    const company = store.get('authStore@currentCompany')
    if (company.addresses.length === 0) {
      return showError('Cadastre um endereço da empresa para continuar')
    }

    state.loading = true
    store.dispatch('purchaseStore/getById', id)
      .then(res => store.dispatch('purchaseStore/generatePDF', { purchase: res.data }).catch(showError),
      )
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  generatePDF: async (context, { purchase, outputType = 'dataurlnewwindow' }) => {
    const [companyRes, providerRes] = await Promise.all([
      store.dispatch('companyStore/getById', purchase.companyId),
      store.dispatch('providerStore/getById', purchase.providerId),
    ])

    const company = companyRes.data
    const provider = providerRes.data
    // const qrcode = await QRCode.toDataURL(purchase.trackingUrl)

    return pdfTemplate({
      outputType,
      returnJsPDFDocObject: true,
      fileName: `Compra: ${purchase.id}`,
      orientationLandscape: false,
      compress: true,
      logo: {
        src: company.includeLogo ? (company.logoSmallUrl || company.logoSmall) : '',
        width: 33.33, // aspect ratio = width/height
        height: 26.66,
        margin: {
          top: 0, // negative or positive num, from the current position
          left: 0, // negative or positive num, from the current position
        },
      },
      // stamp: {
      //   inAllPages: true,
      //   src: qrcode,
      //   width: 20, //aspect ratio = width/height
      //   height: 20,
      //   margin: {
      //     top: 0, //negative or positive num, from the current position
      //     left: 0, //negative or positive num, from the current position
      //   },
      // },
      business: {
        name: company.name,
        address: company.addresses[0].addressLine,
        phone: company.phone,
        email: company.email,
        email_1: company.cnpj,
        website: company.siteUrl,
      },
      contact: {
        label: 'Compra realizada com:',
        name: provider.name,
        address: provider.addresses[0].addressLine,
        phone:
          provider.phone || provider.phone2 || 'Nenhum contato informado',
        email: provider.email || provider.email2 || 'Nenhum email informado',
        otherInfo: provider.cpfCnpj,
      },
      invoice: {
        label: 'Compra #: ',
        num: purchase.id,
        invDate: `Data de Entrega: ${formatToDate(
          new Date(purchase.deliveryDate),
        )}`,
        invGenDate: `Data da Compra: ${formatToDateTime(
          new Date(purchase.createdAt),
        )}`,
        headerBorder: true,
        tableBodyBorder: true,
        header: [
          {
            title: '#',
            style: {
              width: 10,
            },
          },
          {
            title: 'Produto',
            style: {
              width: 80,
            },
          },
          { title: 'Quantidade' },
          { title: 'Preço' },
          { title: 'Desconto' },
          { title: 'Vlr. Frete' },
          { title: 'Vlr. Total' },
        ],
        table: purchase.products.map((i) => [
          i.sequence.toString(),
          i.productName,
          formatToNumber(i.amount),
          formatToBRL(i.price),
          formatToBRL(i.discount),
          formatToBRL(i.shippingValue),
          formatToBRL(i.total),
        ]),
        additionalRows: [
          {
            col1: 'SubTotal:',
            col3: formatToBRL(purchase.valueProducts),
            style: {
              fontSize: 10, // optional, default 12
            },
          },
          {
            col1: 'Desconto:',
            col3: formatToBRL(purchase.discount),
            style: {
              fontSize: 10, // optional, default 12
            },
          },
          {
            col1: 'Vlr. de Frete:',
            col3: formatToBRL(purchase.shippingValue),
            style: {
              fontSize: 10, // optional, default 12
            },
          },
          {
            col1: 'Total:',
            col3: formatToBRL(purchase.value),
            style: {
              fontSize: 14, // optional, default 12
            },
          },
        ],

        invDescLabel: 'Observações',
        invDesc:
          purchase.observation ||
          'Nenhuma observação adicionada a esta compra',
      },
      footer: {
        text: 'Criado por Campag ERP',
      },
      pageEnable: true,
      pageLabel: 'Página ',
    })
  },

  get: async ({ state }, filter = state.filter) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/purchase?${getQueryFromFilter(filter)}`)
      .then(res => {
        state.purchases = res.data

        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getById: (context, id) => axios.get(`${process.env.VUE_APP_API_URL}/purchase/${id}`).catch(showError),

  getFiscalOperations: (context, purchaseId) => axios.get(`${process.env.VUE_APP_API_URL}/purchase/${purchaseId}/fiscal-operations?${getQueryFromFilter({})}`)
    .then(res => res.data)
    .catch(showError),

  purchaseReturn: ({ state }, { purchaseId, items, justification, buyOrder, cfop, fiscalOperationType, fiscalOperationNature }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/purchase/${purchaseId}/return?${getQueryFromFilter({})}`, { items, justification, buyOrder, cfop, fiscalOperationType, fiscalOperationNature })
      .then(res => {
        showSuccess('Devolucao fiscal enfileirada com sucesso!')
        store.dispatch('purchaseStore/get')
        return res.data || true
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  retryPurchaseReturn: ({ state }, { purchaseId, operationId, confirmedNotTransmitted }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/purchase/${purchaseId}/return/${operationId}/retry`, { confirmedNotTransmitted })
      .then(res => {
        showSuccess('Nova tentativa de emissão enviada ao emissor!')
        return res.data || true
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  remove: ({ state }, id) => {
    state.loading = true
    return axios.delete(`${process.env.VUE_APP_API_URL}/purchase/${id}`)
      .then(_ => {
        store.dispatch('purchaseStore/get')
        showSuccess('Compra excluída com sucesso!')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },
}

const getters = make.getters(state)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
